import { useQuery } from "@apollo/client";
import type {
  Organization,
  OrganizationsConnection,
  OrganizationsOrderBy,
} from "@casey/db-types";
import { SEARCH_ALL_ORGANIZATIONS } from "../gql";

interface SearchAllOrganizationsVariables {
  search: string;
  first?: number;
  after?: string;
  orderBy?: OrganizationsOrderBy[];
}

interface SearchAllOrganizationsResponse {
  organizations: OrganizationsConnection;
}

export function useSearchAllOrganizations(
  search: string,
  options?: {
    pageSize?: number;
    orderBy?: OrganizationsOrderBy[];
  },
) {
  const { data, loading, error, refetch, fetchMore } = useQuery<
    SearchAllOrganizationsResponse,
    SearchAllOrganizationsVariables
  >(SEARCH_ALL_ORGANIZATIONS, {
    variables: {
      search,
      first: options?.pageSize || 50,
      orderBy: options?.orderBy || ["CREATED_AT_DESC" as OrganizationsOrderBy],
    },
    skip: !search || search.trim() === "",
    errorPolicy: "all",
  });

  const organizations: Organization[] =
    data?.organizations?.nodes?.filter(
      (org): org is Organization => org !== null,
    ) || [];
  const totalCount = data?.organizations?.totalCount || 0;
  const pageInfo = data?.organizations?.pageInfo;

  const loadMore = () => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;

    return fetchMore({
      variables: {
        after: pageInfo.endCursor,
      },
    });
  };

  return {
    organizations,
    totalCount,
    hasMore: pageInfo?.hasNextPage || false,
    loading,
    error,
    refetch,
    loadMore,
  };
}

export type { Organization };
